const SVG_NS = "http://www.w3.org/2000/svg";

/** Builds the "Drawing Conclusions" wordmark as a real inline <svg> (not a
 * background-image or CSS mask), so its <text> is part of the page document
 * and can use the page's own registered Marimpa @font-face (see main.css) -
 * an externally-referenced SVG resource (mask-image, <img src>, etc.) renders
 * in its own isolated context and can't see the host page's @font-face rules,
 * which is why the logo silently fell back to a generic font before this.
 * `fill="currentColor"` on the root plus `color: var(--accent)` in CSS lets it
 * re-theme exactly like the plain-text title it replaced.
 *
 * The text is written as one plain string - Marimpa handles its own letter
 * spacing, no manual per-letter positioning needed. The viewBox is fitted to
 * the text's actual rendered bounds (see fitViewBoxToText) rather than a
 * fixed hand-measured box, since a fixed box sized for one font's metrics
 * clips or leaves a gap the moment the rendered width doesn't match - which
 * is exactly what happened when the old per-letter tspan positioning (tuned
 * for a specific layout) was replaced by plain, naturally-flowing text.
 * Built via createElementNS rather than innerHTML, per this codebase's
 * convention of never parsing markup strings for anything the app itself
 * constructs. */
export function buildLogoSvg(rootClassName: string): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("role", "img");
  svg.setAttribute("aria-label", "Drawing Conclusions");
  svg.setAttribute("class", rootClassName);
  // Placeholder, generously sized so nothing clips during the brief window
  // before fitViewBoxToText can run (needs the element connected to the
  // document, which only happens once the caller appends our return value).
  svg.setAttribute("viewBox", "0 0 700 70");

  const text = document.createElementNS(SVG_NS, "text");
  text.setAttribute("class", "logo-text");
  text.setAttribute("x", "0");
  text.setAttribute("y", "0");
  text.appendChild(document.createTextNode("Drawing Conclusions"));
  svg.appendChild(text);

  // Runs once fonts are settled, so the measurement reflects Marimpa's real
  // metrics rather than whatever fallback font-display: swap was showing at
  // the moment this element happened to get measured.
  document.fonts.ready.then(() => fitViewBoxToText(svg, text));

  return svg;
}

/** Sizes the SVG's viewBox to exactly fit the text's actual rendered glyph
 * bounds, with a small margin - no clipping, no excess whitespace, and no
 * dependence on a hand-guessed box that only matches one specific layout.
 * getBBox() needs layout, so this only does anything once the element is
 * actually connected to the document. */
function fitViewBoxToText(svg: SVGSVGElement, text: SVGTextElement): void {
  if (!svg.isConnected) return;
  const bbox = text.getBBox();
  if (bbox.width === 0 || bbox.height === 0) return;
  const margin = 4;
  svg.setAttribute(
    "viewBox",
    `${bbox.x - margin} ${bbox.y - margin} ${bbox.width + margin * 2} ${bbox.height + margin * 2}`
  );
}
